import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { format } from 'date-fns';
import { OptimizedImage } from '../../components/common/OptimizedImage';
import { getCardImageUrl } from '../../utils/imageOptimization';

interface NewsItem {
  id: string;
  title: string;
  content: string;
  imageUrl?: string;
  imageFocalPointY?: number | null;
  type: 'NEWS' | 'OFFER';
  storeLocation?: string;
  createdAt: string;
}

const FeedPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [news, setNews] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNews();
  }, [i18n.language]); // Refetch when language changes

  // Prefetch next page images for better performance
  useEffect(() => {
    if (news.length > 0) {
      // Prefetch first 3 images from the feed
      news.slice(0, 3).forEach((item) => {
        if (item.imageUrl) {
          const link = document.createElement('link');
          link.rel = 'prefetch';
          link.as = 'image';
          link.href = getCardImageUrl(item.imageUrl);
          document.head.appendChild(link);
        }
      });
    }
  }, [news]);

  const fetchNews = async () => {
    try {
      // Get current language code (e.g., 'en' or 'es')
      const language = i18n.language.split('-')[0]; // Extract base language (en, es) from 'en-US' or 'es-MX'
      const response = await api.get('/news', {
        params: { 
          page: 1, 
          limit: 20,
          language: language // Send language preference to backend
        },
      });
      setNews(response.data.news || []);
    } catch (error) {
      console.error('Error fetching news:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">{t('feed.title')}</h1>

      {news.length > 0 && (
        <div className="space-y-6">
          {news.map((item) => (
            <div key={item.id} className="bg-white rounded-lg shadow overflow-hidden">
              {item.imageUrl && (
                <Link to={`/feed/news/${item.id}`} className="block w-full h-48 overflow-hidden cursor-pointer hover:opacity-90 transition-opacity">
                  <OptimizedImage
                    src={item.imageUrl}
                    alt={item.title}
                    className="w-full h-full"
                    optimization={{ width: 600, quality: 80 }}
                    loading="lazy"
                    placeholder="skeleton"
                    objectPosition={item.imageFocalPointY != null ? `center ${item.imageFocalPointY * 100}%` : undefined}
                  />
                </Link>
              )}
              <div className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-xs font-semibold px-2 py-1 rounded ${
                    item.type === 'NEWS' 
                      ? 'bg-blue-100 text-blue-800' 
                      : 'bg-green-100 text-green-800'
                  }`}>
                    {item.type === 'NEWS' ? t('feed.news') : t('feed.offers')}
                  </span>
                  <span className="text-sm text-gray-500">
                    {format(new Date(item.createdAt), 'MMM d, yyyy')}
                  </span>
                </div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  <Link to={`/feed/news/${item.id}`} className="hover:text-primary-600">
                    {item.title}
                  </Link>
                </h2>
                <p className="text-gray-700 whitespace-pre-line line-clamp-3">{item.content}</p>
                <Link
                  to={`/feed/news/${item.id}`}
                  className="inline-block mt-2 text-primary-600 hover:text-primary-700 text-sm font-medium"
                >
                  {t('feed.readMore')} →
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FeedPage;
